import React from 'react';
import {useTranslation} from 'react-i18next';
import dayjs from 'dayjs';
import * as utc from 'dayjs/plugin/utc';
import {Housing} from '@guestapp/sdk';
import {
  LOCKS_CONDITIONS,
  TIMING_VALIDATIONS,
  smartLockHoursBeforeCheckin,
  BOOLEAN_TIMING_CONDITIONS_ENUM,
  LOCK_TIMING_CONDITIONS_ENUM,
  LOCK_CONDITIONS_ENUM,
} from './constanst';
import {checkIfReservationIsBeforeTimeRange, getTimeErrorMessage} from './utils';
import {useReservation} from 'context/reservation';
import {useSummary} from 'context/summary';
import {useGuestGroup} from 'hooks/useGuestGroup';
import {useFetchLockConditions} from './useFetchLockConditions';

function useAvailableLocks() {
  dayjs.extend(utc.default);
  const {housing} = useReservation();
  const {data: summary, areAllPaymentsCompleted} = useSummary();
  const {t} = useTranslation();
  const {
    allGuestsRegistered,
    isLeaderGuestRegistered,
    isCurrentRegisteredGuestsCompleted,
    isAllRegisteredGuestIdentified,
  } = useGuestGroup();

  const {data: lockConditions, isLoading: areLockConditionsLoading} =
    useFetchLockConditions();

  const getOpenTimeForSmartLockRangeDays = React.useCallback(
    (currenOpenTime: Record<string, number>) => {
      if (!housing?.smart_lock_init_range_days) return currenOpenTime;
      const rangeDaysToHours = housing?.smart_lock_init_range_days * 24;
      const currentOpenTimeInHours = Object.values(currenOpenTime)[0];
      return currentOpenTimeInHours < rangeDaysToHours
        ? currenOpenTime
        : {smart_lock_init_range_days: rangeDaysToHours};
    },
    [housing?.smart_lock_init_range_days],
  );

  const currentConditions = React.useMemo(
    function getActiveConditions() {
      const activeConditions: Record<string, boolean> = {};
      let timingConditions: Record<string, number> = {};
      if (housing && Object.keys(housing).length === 0) {
        return undefined;
      }
      for (const i in housing) {
        const housingPropertyValue = housing[i as keyof Housing];
        if (
          LOCKS_CONDITIONS.includes(i as LOCK_CONDITIONS_ENUM) &&
          housingPropertyValue &&
          typeof housingPropertyValue === 'boolean'
        ) {
          if (TIMING_VALIDATIONS.includes(i as LOCK_TIMING_CONDITIONS_ENUM)) {
            if (!Boolean(Object.keys(timingConditions).length)) {
              timingConditions[i] =
                smartLockHoursBeforeCheckin[i as BOOLEAN_TIMING_CONDITIONS_ENUM];
            } else {
              const currentTimingCondition = Object.values(timingConditions)[0];
              const newTimingCondition =
                smartLockHoursBeforeCheckin[i as BOOLEAN_TIMING_CONDITIONS_ENUM];
              if (currentTimingCondition > newTimingCondition) {
                timingConditions = {
                  [i]: newTimingCondition,
                };
              }
            }
            continue;
          }
          activeConditions[i] = housingPropertyValue;
        }
      }

      timingConditions = getOpenTimeForSmartLockRangeDays(timingConditions);

      return {...activeConditions, ...timingConditions};
    },
    [housing, getOpenTimeForSmartLockRangeDays],
  );

  const getInvalidTimeConditions = React.useCallback(
    (condition: string, invalidTimeConditions: JSX.Element[] = []) => {
      const hoursBefore = currentConditions?.[condition] as number;
      if (currentConditions?.[condition]) {
        if (!checkIfReservationIsBeforeTimeRange(summary?.check_in_date, hoursBefore)) {
          invalidTimeConditions.push(getTimeErrorMessage(hoursBefore, t));
        }
      }
      return invalidTimeConditions;
    },
    [currentConditions, summary?.check_in_date, t],
  );

  const invalidConditions = React.useMemo(() => {
    if (!currentConditions) return undefined;
    const conditions = Object.keys(currentConditions);
    if (!conditions.length) return undefined;
    let invalidTimeConditions: JSX.Element[] = [];
    const registerConditions: JSX.Element[] = [];
    conditions.forEach(condition => {
      if (TIMING_VALIDATIONS.includes(condition as LOCK_TIMING_CONDITIONS_ENUM)) {
        invalidTimeConditions = getInvalidTimeConditions(
          condition,
          invalidTimeConditions,
        );
      }
      if (condition === LOCK_CONDITIONS_ENUM.afterAllGuestsRegisteredEnabled) {
        if (!allGuestsRegistered) {
          registerConditions.push(<>{t('all_guest_are_registered')}</>);
        }
      }
      if (condition === LOCK_CONDITIONS_ENUM.afterLeadGuestRegisteredEnabled) {
        if (!isLeaderGuestRegistered) {
          registerConditions.push(<>{t('lead_guest_is_registered')}</>);
        }
      }
      if (condition === LOCK_CONDITIONS_ENUM.afterLeadGuestPassedBiomatchEnabled) {
        if (!isCurrentRegisteredGuestsCompleted) {
          registerConditions.push(<>{t('lead_guest_is_registered_and_identified')}</>);
        }
      }
      if (condition === LOCK_CONDITIONS_ENUM.afterAllGuestsPassedBiomatchEnabled) {
        if (!isAllRegisteredGuestIdentified) {
          registerConditions.push(<>{t('all_guest_are_identified')}</>);
        }
      }
      if (condition === LOCK_CONDITIONS_ENUM.afterAllPaymentsComplete) {
        if (!areAllPaymentsCompleted) {
          registerConditions.push(<>{t('all_booking_payments_are_completed')}</>);
        }
      }
      if (condition === LOCK_CONDITIONS_ENUM.allPaymentAndAllGuestsRegistered) {
        if (!areAllPaymentsCompleted || !allGuestsRegistered) {
          registerConditions.push(
            <>{t('all_booking_payments_are_completed_and_all_guest_registered')}</>,
          );
        }
      }
      if (condition === LOCK_CONDITIONS_ENUM.allPaymentAndLeadGuestsIdentified) {
        if (!areAllPaymentsCompleted || !allGuestsRegistered) {
          registerConditions.push(
            <>{t('all_booking_payments_are_completed_and_lead_guest_identified')}</>,
          );
        }
      }
      if (condition === LOCK_CONDITIONS_ENUM.allPaymentAndAllGuestsIdentified) {
        if (!areAllPaymentsCompleted || !isAllRegisteredGuestIdentified) {
          registerConditions.push(
            <>{t('all_booking_payments_are_completed_and_all_guest_identified')}</>,
          );
        }
      }
    });
    return [...invalidTimeConditions, ...registerConditions];
  }, [
    currentConditions,
    getInvalidTimeConditions,
    allGuestsRegistered,
    isLeaderGuestRegistered,
    isAllRegisteredGuestIdentified,
    isCurrentRegisteredGuestsCompleted,
    t,
    areAllPaymentsCompleted,
  ]);

  const areKeysAvailable = Boolean(
    invalidConditions?.length === 0 && lockConditions && lockConditions?.is_available,
  );

  return {
    invalidConditions,
    areKeysAvailable,
    areLockConditionsLoading: areLockConditionsLoading || !housing?.id,
    lockConditions,
  };
}

export {useAvailableLocks};
